/**
 * @file The Component contains the input for Intelligence Server Configuration
 * @author Haonan Yu(haoyu@microStrategy.com)
 */

import React from 'react';
import { Form, Input, Checkbox, Row, Col, Divider } from 'antd';
//data & funcs
import { setFormFieldValue } from '../../../../Utils/utils';
import { WIN_REMOTEREPOSITORY } from '../../../../Utils/Constants';
import { getPageObjectByName } from '../../../../Utils/PageObjectsMapUtil';
//components
import ButtonBar from '../../../ButtonBar/MainButtonBar/MainButtonBar';
import Navigation from '../../../Navigation/Navigation';
import ReferencePicture from '../../../ReferencePicture/ReferencePicture';
//css
import 'antd/dist/antd.css';
import '../../../../Utils/CommonCss.css';

const PAGE_NAME = WIN_REMOTEREPOSITORY;

class IntelligenceServerConfiguration extends React.Component {
    constructor(props) {
        super(props);
        this.formSections = getPageObjectByName(PAGE_NAME).formSections;
    }

    nextStep = () => {
        this.props.form.validateFieldsAndScroll((err, values) => {
            if (!err) {
                this.props.nextStep();
            }
        });
    };

    backStep = () => {
        this.props.backStep();
    };

    /**
     * To compare the password toward previous input password,
     * this is a function provided by 'antd' form validator
     * @param {Object} rule required parameter for the form validator
     * @param {String} value the current field value
     * @param {callback} callback function*
     */
    compareToISPassword = (rule, value, callback) => {
        const { form } = this.props;
        if (value && value !== form.getFieldValue('isPassword')) {
            callback('Two passwords that you enter is inconsistent!');
        } else {
            callback();
        }
    };

    /**
     *
     * Generate a list of form JSX item based on given data
     * @param {Array} formInfo an array that contains necessary content for each form item
     * @return {Array} an array of form items that contain input boxes
     */
    generateFormContent = (formInfo) => {
        const { getFieldDecorator } = this.props.form;
        return formInfo.map((section, index) => (
            <Form.Item
                label={section.label}
                key={index}
                className={section.fieldName}
            >
                {getFieldDecorator(section.fieldName, {
                    rules: [
                        {
                            required: section.rules[0].required,
                            message: section.rules[0].message,
                            whitespace: section.rules[0].whitespace,
                            pattern: section.rules[0].pattern,
                        },
                    ],
                })(
                    section.passwd ? (
                        <Input.Password
                            onChange={(e) => {
                                this.props.setOriginState(
                                    section.state,
                                    e.target.value
                                );
                            }}
                        />
                    ) : (
                        <Input
                            onChange={(e) => {
                                this.props.setOriginState(
                                    section.state,
                                    e.target.value
                                );
                            }}
                        />
                    )
                )}
            </Form.Item>
        ));
    };

    componentDidMount() {
        for (let formInfo of this.formSections.remoteRepoConfig) {
            setFormFieldValue(formInfo.fieldName, formInfo.state, this.props);
        }
    }

    render() {
        let paOverrideCheckbox = null;
        let collabOverrideCheckbox = null;
        let storageServiceOverrideCheckbox = null;
        let remoteConfigContent = null;
        if (this.props.getOriginState('platformAnalyticsSelect')) {
            paOverrideCheckbox = (
                <Checkbox
                    onChange={(e) => {
                        this.props.setOriginState(
                            'overrideExistingRepositoryPA',
                            e.target.checked
                        );
                    }}
                    checked={this.props.getOriginState(
                        'overrideExistingRepositoryPA'
                    )}
                >
                    Override Platform Analytics Database if exists
                </Checkbox>
            );
        }

        if (this.props.getOriginState('storageServiceSelect')) {
            storageServiceOverrideCheckbox = (
                <Checkbox
                    onChange={(e) => {
                        this.props.setOriginState(
                            'overrideExistingRepositoryStorageService',
                            e.target.checked
                        );
                    }}
                    checked={this.props.getOriginState(
                        'overrideExistingRepositoryStorageService'
                    )}
                >
                    Override Storage Service Database if exists
                </Checkbox>
            );
        }

        if (this.props.getOriginState('collaborationSelect')) {
            collabOverrideCheckbox = (
                <Checkbox
                    onChange={(e) => {
                        this.props.setOriginState(
                            'overrideExistingRepositoryCollab',
                            e.target.checked
                        );
                    }}
                    checked={this.props.getOriginState(
                        'overrideExistingRepositoryCollab'
                    )}
                >
                    Override Collaboration Database if exists
                </Checkbox>
            );
        }

        if (!this.props.getOriginState('microStrategyRepositorySelect')) {
            remoteConfigContent = (
                <div>
                    <br />
                    <br />
                    <div className="info-tag">
                        <img
                            className="info-icon"
                            alt="info icon"
                            src={require('../../../../assets/Icons/info.svg').default}
                        />
                        To use a remote repository, provide the connectivity
                        information to be used.{' '}
                        <a
                            href="http://www2.microstrategy.com/producthelp/Current/InstallConfig/en-us/Content/remote_repository.htm"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Learn more.
                        </a>
                    </div>
                    {this.generateFormContent(
                        this.formSections.remoteRepoConfig
                    )}
                    {paOverrideCheckbox}
                    {collabOverrideCheckbox}
                    {storageServiceOverrideCheckbox}
                </div>
            );
        }
        return (
            <Form className="form">
                <Row type="flex" align="top">
                    <Col
                        span={4}
                        className={['colNav', 'winIntelligNavHeight'].join(' ')}
                    >
                        <p className="headlineTop">Steps</p>
                        <Navigation
                            validateForm={
                                this.props.form.validateFieldsAndScroll
                            }
                            setOriginState={this.props.setOriginState}
                            getOriginState={this.props.getOriginState}
                            steps={this.props.steps}
                            deepestStep={this.props.deepestStep}
                            useOs={this.props.useOs}
                        />
                    </Col>

                    <Col span={12} className="colContent">
                        <p className="headlineTop">Remote Repository</p>
                        <div>
                            <Checkbox
                                onChange={(e) => {
                                    this.props.setOriginState(
                                        'microStrategyRepositorySelect',
                                        e.target.checked
                                    );
                                }}
                                checked={this.props.getOriginState(
                                    'microStrategyRepositorySelect'
                                )}
                            >
                                Use MicroStrategy Repository
                            </Checkbox>

                            {remoteConfigContent}
                        </div>
                    </Col>

                    <Divider
                        className="divider"
                        type="vertical"
                        orientation="right"
                    />
                    <Col span={7} className="colPics">
                        <p className="headlineTop">Reference Pictures</p>
                        <ReferencePicture
                            pageName={PAGE_NAME}
                            getOriginState={this.props.getOriginState}
                            useVersion={this.props.useVersion}
                        />
                    </Col>
                </Row>
                <ButtonBar
                    useOs={this.props.useOs}
                    clickNext={this.nextStep}
                    clickBack={this.backStep}
                />
            </Form>
        );
    }
}

const IntelligenceServerSetting = Form.create({
    name: 'intelligenceServerConfiguration',
})(IntelligenceServerConfiguration);
export default IntelligenceServerSetting;
